import { Plugin } from "@ckeditor/ckeditor5-core";
import { FileRepository } from "@ckeditor/ckeditor5-upload";

export default class ImageUploadAdapterPlugin extends Plugin {
    /**
     * @inheritDoc
     */
    static get requires() {
        return [FileRepository];
    }
    /**
     * @inheritDoc
     */
    static get pluginName() {
        return 'ImageUploadAdapter';
    }

    /**
     * @inheritDoc
     */
    init() {
        const options = {
            ...this.editor.config.get('fpoptions'),
            ...this.editor.config.get('images.fpoptions')
        };
        if (!options) {
            return;
        }
        if (!options.url) {
            console.warn('image-upload-adapter-missing-uploadurl');
            return;
        }
        if (!options.itemid) {
            console.warn('image-upload-adapter-missing-itemid');
            return;
        }
        if (!options.sesskey) {
            console.warn('image-upload-adapter-missing-sesskey');
            return;
        }
        if (!options.repositoryid) {
            console.warn('image-upload-adapter-missing-repositoryid');
            return;
        }

        this.editor.plugins.get('FileRepository').createUploadAdapter = (loader) => {
            return new ImageUploadAdapter(loader, options);
        };
    }
}

class ImageUploadAdapter {
    constructor(loader, options) {
        // The file loader instance to use during the upload.
        this.loader = loader;
        this.options = options;
    }

    // Starts the upload process.
    upload() {
        return this.loader.file
            .then(file => new Promise((resolve, reject) => {
            this._initRequest();
            this._initListeners(resolve, reject, file);
            this._sendRequest(file);
        }));
    }

    abort() {
        if (this.xhr) {
            this.xhr.abort();
        }
    }

    _initRequest() {
        const xhr = this.xhr = new XMLHttpRequest();
        var uploadUrl = this.options.url;
        if (uploadUrl.indexOf('?') !== -1) {
            uploadUrl += '&action=upload';
        } else {
            uploadUrl += '?action=upload';
        }
        xhr.open("POST", uploadUrl, true);
    }

    _initListeners(resolve, reject, file) {
        const xhr = this.xhr;
        const loader = this.loader;
        const genericErrorText = `Couldn't upload file: ${file.name}.`;
        xhr.addEventListener('error', () => reject(genericErrorText));
        xhr.addEventListener('abort', () => reject());
        xhr.addEventListener('load', () => {
            const response = JSON.parse(xhr.responseText);
            if (!response || response.error) {
                return reject(response && response.error && response.error.message ? response.error.message : genericErrorText);
            } else {
                if (response.event == 'fileexists') {
                    // Do not worry about this, as we only care about the last
                    // file uploaded, with the filepicker
                    response.file = response.newfile.filename;
                    response.url = response.newfile.url;
                }
            }
            const urls = response.url ? { default: response.url } : response.urls;
            // Resolve with the normalized `urls` property and pass the rest of the response
            // to allow customizing the behavior of features relying on the upload adapters.
            resolve({
                ...response,
                urls
            });
        });
        // Upload progress when it is supported.
        /* istanbul ignore else -- @preserve */
        if (xhr.upload) {
            xhr.upload.addEventListener('progress', evt => {
                if (evt.lengthComputable) {
                    loader.uploadTotal = evt.total;
                    loader.uploaded = evt.loaded;
                }
            });
        }
    }
    _sendRequest(file) {
        // Set headers if specified.
        const headers = this.options.headers || {};
        // Use the withCredentials flag if specified.
        const withCredentials = this.options.withCredentials || false;
        for (const headerName of Object.keys(headers)) {
            this.xhr.setRequestHeader(headerName, headers[headerName]);
        }
        this.xhr.withCredentials = withCredentials;

        // Prepare the form data.
        const data = new FormData();
        data.append('repo_upload_file', file); // The FormData class allows us to attach a file
        data.append('sesskey', this.options.sesskey);
        data.append('repo_id', this.options.repositoryid);
        data.append('itemid', this.options.itemid);
        data.append('savepath', '/');
        data.append('title', file.name);
        data.append('author', this.options.author);
        data.append('ctx_id', this.options.contextid);
        data.append('maxbytes', this.options.maxbytes);
        data.append('areamaxbytes', this.options.areamaxbytes);
        data.append('env', this.options.env);

        for (var i=0; i<this.options.accepted_types.length; i++) {
            data.append('accepted_types[]', this.options.accepted_types[i]);
        }

        // Send the request.
        this.xhr.send(data);
    }
}